/**
 * InterviewToolkit — Phase 4 (2026-02).
 *
 * One dialog surfaced from the candidate drawer with three sub-tabs:
 *   1. Scorecard   → fill an HR-built rubric for phone screen or interview
 *   2. Email       → pick a template, tweak, send via Gmail (Resend fallback)
 *   3. Schedule    → book a Google Calendar event (Meet link optional)
 */
import React, { useEffect, useMemo, useState } from 'react';
import api, { errMsg } from '../../lib/api';
import { fmtDateTime } from '../../lib/date';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../ui/dialog';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Card } from '../ui/card';
import { Popover, PopoverContent, PopoverTrigger } from '../ui/popover';
import { Calendar } from '../ui/calendar';
import { ClipboardList, Mail, CalendarClock, Send, Video, Clock, CheckCircle2, CalendarDays } from 'lucide-react';
import { toast } from 'sonner';

const C = {
  ink: '#2D2833', ink2: '#685B72', soft: '#8A7D95',
  border: '#E8E5EC', paper: '#FBF8F4',
  plum: '#4A3B5F', gold: '#B08A3A', teal: '#3B6F5D', rose: '#8A3A3A',
};

// ─── Scorecard tab ────────────────────────────────────────────
function ScorecardTab({ candidate, onSubmitted }) {
  const [stage, setStage] = useState('phone_screen');
  const [templates, setTemplates] = useState([]);
  const [selectedId, setSelectedId] = useState('');
  const [answers, setAnswers] = useState({});
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState(false);
  const [history, setHistory] = useState([]);

  useEffect(() => {
    const role = candidate?.applied_role || 'SLP';
    api.get(`/hr/scorecard-templates?role=${encodeURIComponent(role)}&stage=${stage}`)
      .then(r => setTemplates(r.data.templates || []))
      .catch(() => setTemplates([]));
    api.get(`/hr/candidates/${candidate.candidate_id}/scorecards`)
      .then(r => setHistory(r.data.scorecards || []))
      .catch(() => setHistory([]));
    setSelectedId(''); setAnswers({}); setNote('');
  }, [candidate?.candidate_id, candidate?.applied_role, stage]);

  const tpl = useMemo(() => templates.find(t => t.id === selectedId), [templates, selectedId]);
  const setAnswer = (qid, patch) => setAnswers(a => ({ ...a, [qid]: { ...(a[qid] || {}), ...patch } }));

  const submit = async () => {
    if (!tpl) return toast.error('Pick a template');
    setBusy(true);
    try {
      const payload = {
        template_id: tpl.id,
        stage_context: stage,
        overall_note: note,
        answers: tpl.questions.map(q => ({
          question_id: q.id,
          value: answers[q.id]?.value ?? (q.kind === 'text' ? '' : 0),
          note: answers[q.id]?.note || '',
        })),
      };
      const { data } = await api.post(`/hr/candidates/${candidate.candidate_id}/scorecards`, payload);
      toast.success(`Scorecard submitted — ${data.pct != null ? `${data.pct}%` : 'saved'}`);
      onSubmitted?.(data);
      setSelectedId(''); setAnswers({}); setNote('');
      const r = await api.get(`/hr/candidates/${candidate.candidate_id}/scorecards`);
      setHistory(r.data.scorecards || []);
    } catch (e) {
      toast.error(errMsg(e, 'Could not submit'));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="space-y-3">
      <div className="grid grid-cols-2 gap-2">
        <div>
          <Label className="text-[11px]">Stage</Label>
          <Select value={stage} onValueChange={setStage}>
            <SelectTrigger data-testid="sc-stage"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="phone_screen">Phone screen</SelectItem>
              <SelectItem value="interview">Interview</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label className="text-[11px]">Template ({candidate?.applied_role || 'SLP'})</Label>
          <Select value={selectedId} onValueChange={setSelectedId} disabled={!templates.length}>
            <SelectTrigger data-testid="sc-template">
              <SelectValue placeholder={templates.length ? 'Choose…' : 'No templates for this role/stage'} />
            </SelectTrigger>
            <SelectContent>
              {templates.map(t => <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
      </div>

      {tpl && (
        <Card className="p-3 space-y-3 border" style={{ borderColor: C.border }}>
          {tpl.questions.map(q => (
            <div key={q.id} data-testid={`sc-q-${q.id}`}>
              <Label className="text-[12px]" style={{ color: C.ink }}>{q.prompt}</Label>
              {q.kind === 'rating' && (
                <div className="flex gap-1 mt-1">
                  {Array.from({ length: q.max_score || 5 }, (_, i) => i + 1).map(n => (
                    <button key={n} type="button"
                            onClick={() => setAnswer(q.id, { value: n })}
                            data-testid={`sc-rating-${q.id}-${n}`}
                            className="w-7 h-7 rounded text-[12px] font-semibold border transition-colors"
                            style={{
                              borderColor: C.border,
                              background: (answers[q.id]?.value === n) ? C.plum : 'transparent',
                              color: (answers[q.id]?.value === n) ? 'white' : C.ink,
                            }}>
                      {n}
                    </button>
                  ))}
                </div>
              )}
              {q.kind === 'yn' && (
                <div className="flex gap-2 mt-1">
                  {['yes', 'no'].map(v => (
                    <button key={v} type="button"
                            onClick={() => setAnswer(q.id, { value: v })}
                            data-testid={`sc-yn-${q.id}-${v}`}
                            className="px-3 py-1 rounded text-[12px] border transition-colors"
                            style={{
                              borderColor: C.border,
                              background: (answers[q.id]?.value === v) ? (v === 'yes' ? C.teal : C.rose) : 'transparent',
                              color: (answers[q.id]?.value === v) ? 'white' : C.ink,
                            }}>
                      {v === 'yes' ? 'Yes' : 'No'}
                    </button>
                  ))}
                </div>
              )}
              {q.kind === 'text' && (
                <Textarea rows={2} className="text-[12px] mt-1"
                          value={answers[q.id]?.value || ''}
                          onChange={e => setAnswer(q.id, { value: e.target.value })}
                          data-testid={`sc-text-${q.id}`} />
              )}
            </div>
          ))}
          <div>
            <Label className="text-[11px]">Overall note</Label>
            <Textarea rows={2} value={note} onChange={e => setNote(e.target.value)}
                      data-testid="sc-overall-note" />
          </div>
          <Button size="sm" onClick={submit} disabled={busy} data-testid="sc-submit"
                  style={{ background: C.plum, color: 'white' }}>
            {busy ? 'Saving…' : 'Save scorecard'}
          </Button>
        </Card>
      )}

      {history.length > 0 && (
        <div>
          <div className="text-[11px] uppercase tracking-widest font-semibold mb-1" style={{ color: C.soft }}>History</div>
          <div className="space-y-1">
            {history.map(h => (
              <div key={h.id} className="text-[12px] flex items-center gap-2 py-1 px-2 rounded"
                   style={{ background: C.paper, border: `1px solid ${C.border}` }}
                   data-testid={`sc-history-${h.id}`}>
                <ClipboardList size={12} style={{ color: C.plum }} />
                <div className="flex-1">
                  <span style={{ color: C.ink }}>{h.template_name}</span>
                  <span className="ml-2 text-[10px]" style={{ color: C.soft }}>{h.stage_context}</span>
                </div>
                <div style={{ color: C.ink }}>{h.pct != null ? `${h.pct}%` : `${h.total_score}/${h.max_score}`}</div>
                <div className="text-[10px]" style={{ color: C.soft }}>{fmtDateTime(h.submitted_at)}</div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

// ─── Email tab ────────────────────────────────────────────────
function EmailTab({ candidate }) {
  const [templates, setTemplates] = useState([]);
  const [selectedId, setSelectedId] = useState('');
  const [override, setOverride] = useState('');
  const [busy, setBusy] = useState(false);
  const [history, setHistory] = useState([]);
  const [extra, setExtra] = useState({ clinic: '', date: '', time: '' });

  useEffect(() => {
    api.get('/hr/email-templates').then(r => setTemplates(r.data.templates || [])).catch(() => setTemplates([]));
    api.get(`/hr/candidates/${candidate.candidate_id}/emails`).then(r => setHistory(r.data.emails || [])).catch(() => setHistory([]));
    setSelectedId(''); setOverride('');
  }, [candidate?.candidate_id]);

  const tpl = useMemo(() => templates.find(t => t.id === selectedId), [templates, selectedId]);

  const send = async () => {
    if (!tpl) return toast.error('Pick a template');
    setBusy(true);
    try {
      const { data } = await api.post(`/hr/candidates/${candidate.candidate_id}/send-email`, {
        template_id: tpl.id,
        to_override: override || undefined,
        extra_context: extra,
      });
      if (data.ok) {
        toast.success(`Email sent via ${data.email.channel}`);
      } else {
        toast.error(`Send failed: ${data.reason}`);
      }
      const r = await api.get(`/hr/candidates/${candidate.candidate_id}/emails`);
      setHistory(r.data.emails || []);
      setSelectedId('');
    } catch (e) {
      toast.error(errMsg(e, 'Send failed'));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="space-y-3">
      <div>
        <Label className="text-[11px]">Template</Label>
        <Select value={selectedId} onValueChange={setSelectedId} disabled={!templates.length}>
          <SelectTrigger data-testid="em-template">
            <SelectValue placeholder={templates.length ? 'Choose…' : 'No email templates yet — create some in HR Settings → Templates'} />
          </SelectTrigger>
          <SelectContent>
            {templates.map(t => (
              <SelectItem key={t.id} value={t.id}>
                {t.name} <span className="text-[10px] ml-1" style={{ color: C.soft }}>{t.category}</span>
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      <div className="grid grid-cols-3 gap-2">
        <div>
          <Label className="text-[11px]">Clinic</Label>
          <Input value={extra.clinic} onChange={e => setExtra(x => ({ ...x, clinic: e.target.value }))}
                 placeholder="Katy" className="text-[12px]" data-testid="em-clinic" />
        </div>
        <div>
          <Label className="text-[11px]">Date</Label>
          <Input value={extra.date} onChange={e => setExtra(x => ({ ...x, date: e.target.value }))}
                 placeholder="Mon Mar 3" className="text-[12px]" data-testid="em-date" />
        </div>
        <div>
          <Label className="text-[11px]">Time</Label>
          <Input value={extra.time} onChange={e => setExtra(x => ({ ...x, time: e.target.value }))}
                 placeholder="10:30 AM CT" className="text-[12px]" data-testid="em-time" />
        </div>
      </div>

      <div>
        <Label className="text-[11px]">To (defaults to candidate.email)</Label>
        <Input value={override} onChange={e => setOverride(e.target.value)}
               placeholder={candidate?.email || 'No email on file'}
               className="text-[12px]" data-testid="em-override" />
      </div>

      {tpl && (
        <Card className="p-3 border text-[12px] space-y-1" style={{ borderColor: C.border, background: C.paper }}>
          <div><span style={{ color: C.soft }}>Subject:</span> <span style={{ color: C.ink }}>{tpl.subject}</span></div>
          <div className="whitespace-pre-wrap" style={{ color: C.ink2 }}>{tpl.body_markdown.slice(0, 300)}{tpl.body_markdown.length > 300 ? '…' : ''}</div>
        </Card>
      )}

      <Button size="sm" onClick={send} disabled={busy || !tpl} data-testid="em-send"
              style={{ background: C.plum, color: 'white' }} className="gap-1">
        <Send size={12} /> {busy ? 'Sending…' : 'Send'}
      </Button>

      {history.length > 0 && (
        <div>
          <div className="text-[11px] uppercase tracking-widest font-semibold mb-1" style={{ color: C.soft }}>Recently sent</div>
          <div className="space-y-1">
            {history.map(h => (
              <div key={h.id} className="text-[12px] flex items-center gap-2 py-1 px-2 rounded"
                   style={{ background: C.paper, border: `1px solid ${C.border}` }}
                   data-testid={`em-history-${h.id}`}>
                <Mail size={12} style={{ color: C.plum }} />
                <div className="flex-1 truncate">
                  <span style={{ color: C.ink }}>{h.subject}</span>
                </div>
                <span className="text-[10px] px-1.5 py-0.5 rounded"
                      style={{
                        background: h.status === 'sent' ? '#E4F1EA' : '#F6E4E4',
                        color: h.status === 'sent' ? C.teal : C.rose,
                      }}>
                  {h.status} · {h.channel}
                </span>
                <div className="text-[10px]" style={{ color: C.soft }}>{fmtDateTime(h.sent_at)}</div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

// ─── Schedule tab ─────────────────────────────────────────────
// 15-min time slots, 6:00 AM → 8:00 PM Central. Users can also
// type their own time (e.g. "9:37 AM") and it gets parsed.
const TIME_SLOTS = (() => {
  const out = [];
  for (let h = 6; h <= 20; h++) {
    for (let m = 0; m < 60; m += 15) {
      const dt = new Date(2020, 0, 1, h, m);
      const label = dt.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' });
      out.push({ value: `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`, label });
    }
  }
  return out;
})();

const DURATIONS = [
  { value: 15, label: '15 min' },
  { value: 30, label: '30 min' },
  { value: 45, label: '45 min' },
  { value: 60, label: '1 hour' },
  { value: 90, label: '1h 30m' },
  { value: 120, label: '2 hours' },
];

function parseTypedTime(str) {
  // Accepts: "10:30 AM", "1030 am", "10:30", "10:30am", "22:15", "13"
  if (!str) return null;
  const s = str.trim().toUpperCase().replace(/\s+/g, '');
  const m = s.match(/^(\d{1,2}):?(\d{2})?(AM|PM)?$/);
  if (!m) return null;
  let h = parseInt(m[1], 10);
  const mi = parseInt(m[2] || '0', 10);
  const ap = m[3];
  if (Number.isNaN(h) || h < 0 || h > 23 || mi < 0 || mi > 59) return null;
  if (ap === 'AM' && h === 12) h = 0;
  else if (ap === 'PM' && h < 12) h += 12;
  return `${String(h).padStart(2, '0')}:${String(mi).padStart(2, '0')}`;
}

function ScheduleTab({ candidate }) {
  const [form, setForm] = useState({
    stage_hint: 'phone_screen',
    date: null,             // JS Date (local)
    startTime: '10:00',     // "HH:MM" 24h
    startTimeText: '',      // freeform typed override
    durationMin: 30,
    title: '',
    delivery: 'meet',       // 'meet' | 'in_person'
    clinic: '',
    address: '',
    attendees_text: '',
    description: '',
  });
  const [dateOpen, setDateOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [history, setHistory] = useState([]);
  const [clinicOpts, setClinicOpts] = useState([]);
  const [hrCcDefault, setHrCcDefault] = useState('');

  useEffect(() => {
    api.get(`/hr/candidates/${candidate.candidate_id}/interviews`)
      .then(r => setHistory(r.data.interviews || []))
      .catch(() => setHistory([]));
    api.get('/hr/calendar/clinic-options')
      .then(r => {
        setClinicOpts(r.data.clinics || []);
        setHrCcDefault(r.data.default_hr_cc || '');
      })
      .catch(() => {});
    setForm(f => ({
      ...f,
      title: `${candidate.applied_role || 'Interview'} — ${candidate.name}`,
    }));
  }, [candidate?.candidate_id, candidate?.name, candidate?.applied_role]);

  const selectedClinicMeta = useMemo(
    () => clinicOpts.find(c => c.name === form.clinic),
    [clinicOpts, form.clinic]
  );

  const effectiveStartTime = useMemo(
    () => parseTypedTime(form.startTimeText) || form.startTime,
    [form.startTime, form.startTimeText]
  );

  const previewISO = useMemo(() => {
    if (!form.date || !effectiveStartTime) return { start: null, end: null };
    const [hh, mm] = effectiveStartTime.split(':').map(Number);
    const start = new Date(form.date);
    start.setHours(hh, mm, 0, 0);
    const end = new Date(start.getTime() + form.durationMin * 60_000);
    return { start, end };
  }, [form.date, effectiveStartTime, form.durationMin]);

  const submit = async () => {
    if (!previewISO.start || !previewISO.end) return toast.error('Pick a date + time');
    if (form.delivery === 'in_person' && !form.clinic && !form.address.trim()) {
      return toast.error('Pick a clinic or type an address');
    }
    setBusy(true);
    try {
      const attendees = form.attendees_text.split(/[,\s]+/).map(s => s.trim()).filter(Boolean);
      const payload = {
        stage_hint: form.stage_hint,
        start_iso: previewISO.start.toISOString(),
        end_iso:   previewISO.end.toISOString(),
        title: form.title,
        description: form.description,
        delivery: form.delivery,
        attendees,
      };
      if (form.delivery === 'in_person') {
        if (form.clinic && form.clinic !== '__other__') payload.clinic = form.clinic;
        if (form.address.trim()) payload.address = form.address.trim();
      }
      const { data } = await api.post(`/hr/candidates/${candidate.candidate_id}/schedule-interview`, payload);
      if (data.calendar_connected) {
        toast.success('Booked on the careers@ Google Calendar — invites sent');
      } else {
        toast.warning('Saved locally. Google Calendar not connected yet — connect it in Setup to auto-invite.');
      }
      const r = await api.get(`/hr/candidates/${candidate.candidate_id}/interviews`);
      setHistory(r.data.interviews || []);
    } catch (e) {
      toast.error(errMsg(e, 'Could not schedule'));
    } finally {
      setBusy(false);
    }
  };

  const dateLabel = form.date
    ? form.date.toLocaleDateString([], { weekday: 'short', month: 'short', day: 'numeric', year: 'numeric' })
    : 'Pick a date';

  return (
    <div className="space-y-3">
      <div className="grid grid-cols-2 gap-2">
        <div>
          <Label className="text-[11px]">Stage</Label>
          <Select value={form.stage_hint} onValueChange={v => setForm(f => ({ ...f, stage_hint: v }))}>
            <SelectTrigger data-testid="sk-stage"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="phone_screen">Phone screen</SelectItem>
              <SelectItem value="interview">Interview</SelectItem>
              <SelectItem value="clinic_visit">Clinic visit</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label className="text-[11px]">Delivery</Label>
          <div className="flex gap-2 mt-1">
            {[
              { key: 'meet',      label: 'Google Meet', icon: Video },
              { key: 'in_person', label: 'In person',   icon: Clock },
            ].map(o => {
              const Icon = o.icon;
              const active = form.delivery === o.key;
              return (
                <button key={o.key}
                        type="button"
                        onClick={() => setForm(f => ({ ...f, delivery: o.key }))}
                        data-testid={`sk-delivery-${o.key}`}
                        className="flex-1 flex items-center justify-center gap-1 rounded border text-[12px] py-1.5 transition-colors"
                        style={{
                          borderColor: active ? C.plum : C.border,
                          background: active ? C.plum : 'transparent',
                          color: active ? 'white' : C.ink,
                          fontWeight: active ? 600 : 400,
                        }}>
                  <Icon size={12} /> {o.label}
                </button>
              );
            })}
          </div>
        </div>
      </div>

      {form.delivery === 'in_person' && (
        <div className="space-y-2 rounded p-2" style={{ background: C.paper, border: `1px solid ${C.border}` }}>
          <div>
            <Label className="text-[11px]">Clinic</Label>
            <Select value={form.clinic} onValueChange={v => setForm(f => ({ ...f, clinic: v, address: '' }))}>
              <SelectTrigger data-testid="sk-clinic"><SelectValue placeholder="Pick a clinic" /></SelectTrigger>
              <SelectContent className="max-w-[500px]">
                {clinicOpts.map(c => (
                  <SelectItem key={c.name} value={c.name}>
                    <div className="flex flex-col">
                      <span style={{ color: C.ink, fontWeight: 500 }}>{c.name}</span>
                      {c.address && (
                        <span className="text-[10px]" style={{ color: C.soft }}>{c.address}</span>
                      )}
                    </div>
                  </SelectItem>
                ))}
                <SelectItem value="__other__">
                  <span style={{ color: C.ink }}>Other</span>
                  <span className="text-[10px] ml-1" style={{ color: C.soft }}>(type an address below)</span>
                </SelectItem>
              </SelectContent>
            </Select>
            {selectedClinicMeta?.address && (
              <div className="text-[10.5px] mt-1" style={{ color: C.soft }}>
                📍 {selectedClinicMeta.address}
              </div>
            )}
          </div>
          {(form.clinic === '__other__' || (selectedClinicMeta && !selectedClinicMeta.address)) && (
            <div>
              <Label className="text-[11px]">
                {form.clinic === '__other__' ? 'Address' : `Address for ${form.clinic}`}
              </Label>
              <Input value={form.address}
                     onChange={e => setForm(f => ({ ...f, address: e.target.value }))}
                     placeholder="123 Main St, Katy, TX 77494"
                     className="text-[12px]" data-testid="sk-address" />
            </div>
          )}
        </div>
      )}

      {/* Date + Time + Duration row */}
      <div className="grid grid-cols-12 gap-2">
        <div className="col-span-5">
          <Label className="text-[11px]">Date</Label>
          <Popover open={dateOpen} onOpenChange={setDateOpen}>
            <PopoverTrigger asChild>
              <button data-testid="sk-date-trigger"
                      className="w-full h-9 rounded border text-[12px] px-3 flex items-center gap-2 hover:bg-gray-50"
                      style={{ borderColor: C.border, color: form.date ? C.ink : C.soft, background: 'white' }}>
                <CalendarDays size={12} style={{ color: C.plum }} /> {dateLabel}
              </button>
            </PopoverTrigger>
            <PopoverContent className="p-0 w-auto" align="start">
              <Calendar
                mode="single"
                selected={form.date}
                onSelect={(d) => { setForm(f => ({ ...f, date: d })); setDateOpen(false); }}
                disabled={(d) => d < new Date(new Date().setHours(0, 0, 0, 0))}
                initialFocus
                data-testid="sk-calendar"
              />
            </PopoverContent>
          </Popover>
        </div>
        <div className="col-span-4">
          <Label className="text-[11px]">Start time</Label>
          <div className="flex gap-1">
            <Select value={form.startTime}
                    onValueChange={v => setForm(f => ({ ...f, startTime: v, startTimeText: '' }))}>
              <SelectTrigger className="flex-1" data-testid="sk-time-preset"><SelectValue /></SelectTrigger>
              <SelectContent className="max-h-[240px]">
                {TIME_SLOTS.map(s => <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <Input
            value={form.startTimeText}
            onChange={e => setForm(f => ({ ...f, startTimeText: e.target.value }))}
            placeholder="or type e.g. 9:37 AM"
            className="text-[11px] h-7 mt-1"
            data-testid="sk-time-typed"
          />
        </div>
        <div className="col-span-3">
          <Label className="text-[11px]">Duration</Label>
          <Select value={String(form.durationMin)}
                  onValueChange={v => setForm(f => ({ ...f, durationMin: Number(v) }))}>
            <SelectTrigger data-testid="sk-duration"><SelectValue /></SelectTrigger>
            <SelectContent>
              {DURATIONS.map(d => <SelectItem key={d.value} value={String(d.value)}>{d.label}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
      </div>

      {previewISO.start && previewISO.end && (
        <div className="text-[11px]" style={{ color: C.soft }} data-testid="sk-preview">
          → {previewISO.start.toLocaleString([], { weekday: 'short', month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' })}
          &nbsp;–&nbsp;
          {previewISO.end.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' })}
          &nbsp;({form.durationMin} min)
        </div>
      )}

      <div>
        <Label className="text-[11px]">Title</Label>
        <Input value={form.title} onChange={e => setForm(f => ({ ...f, title: e.target.value }))}
               className="text-[12px]" data-testid="sk-title" />
      </div>

      <div>
        <Label className="text-[11px]">Extra attendees (candidate + {hrCcDefault || 'HR CC'} + you are auto-added)</Label>
        <Input value={form.attendees_text} onChange={e => setForm(f => ({ ...f, attendees_text: e.target.value }))}
               placeholder="hiring.manager@thrivespeech.net"
               className="text-[12px]" data-testid="sk-attendees" />
      </div>

      <div>
        <Label className="text-[11px]">Notes to attendees</Label>
        <Textarea rows={2} value={form.description} onChange={e => setForm(f => ({ ...f, description: e.target.value }))}
                  className="text-[12px]" data-testid="sk-description" />
      </div>

      <Button size="sm" onClick={submit} disabled={busy} data-testid="sk-submit"
              style={{ background: C.plum, color: 'white' }} className="gap-1">
        <CalendarClock size={12} /> {busy ? 'Booking…' : 'Book interview'}
      </Button>

      {history.length > 0 && (
        <div>
          <div className="text-[11px] uppercase tracking-widest font-semibold mb-1" style={{ color: C.soft }}>Scheduled</div>
          <div className="space-y-1">
            {history.map(h => (
              <div key={h.id} className="text-[12px] flex items-center gap-2 py-1 px-2 rounded"
                   style={{ background: C.paper, border: `1px solid ${C.border}` }}
                   data-testid={`sk-history-${h.id}`}>
                <Clock size={12} style={{ color: C.plum }} />
                <div className="flex-1 truncate">
                  <span style={{ color: C.ink }}>{h.title}</span>
                  <span className="text-[10px] ml-2" style={{ color: C.soft }}>
                    {h.stage_hint} · {h.delivery === 'meet' ? 'Meet' : (h.clinic || 'in-person')}
                  </span>
                </div>
                {h.meet_link && (
                  <a href={h.meet_link} target="_blank" rel="noreferrer"
                     className="text-[10px] underline" style={{ color: C.plum }}>Meet</a>
                )}
                <div className="text-[10px]" style={{ color: C.soft }}>{fmtDateTime(h.start_iso)}</div>
                <span className="text-[10px] px-1.5 py-0.5 rounded"
                      style={{ background: h.calendar_status === 'created' ? '#E4F1EA' : '#F1F0F4', color: C.ink2 }}>
                  {h.calendar_status}
                </span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

// ─── Toolkit dialog ──────────────────────────────────────────
export default function InterviewToolkit({ candidate, open, onOpenChange, onChanged }) {
  const [tab, setTab] = useState('score');
  if (!candidate) return null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="interview-toolkit" className="max-w-2xl max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle style={{ color: C.ink }}>Interview toolkit — {candidate.name}</DialogTitle>
          <DialogDescription className="text-[12px]" style={{ color: C.soft }}>
            Score interviews, send templated emails, and schedule the next meeting on Google Calendar.
          </DialogDescription>
        </DialogHeader>

        <div className="border-b flex gap-1" style={{ borderColor: C.border }}>
          {[
            { key: 'score',  label: 'Scorecard', icon: ClipboardList },
            { key: 'email',  label: 'Email',     icon: Mail },
            { key: 'sched',  label: 'Schedule',  icon: CalendarClock },
          ].map(t => {
            const Icon = t.icon;
            const active = t.key === tab;
            return (
              <button key={t.key} onClick={() => setTab(t.key)}
                      data-testid={`toolkit-tab-${t.key}`}
                      className="px-3 py-2 flex items-center gap-2 text-sm border-b-2 transition-colors"
                      style={{
                        borderColor: active ? C.gold : 'transparent',
                        color: active ? C.ink : C.ink2,
                        fontWeight: active ? 600 : 400,
                      }}>
                <Icon size={13} strokeWidth={1.75} /> {t.label}
              </button>
            );
          })}
        </div>

        <div className="pt-3">
          {tab === 'score' && <ScorecardTab candidate={candidate} onSubmitted={onChanged} />}
          {tab === 'email' && <EmailTab candidate={candidate} />}
          {tab === 'sched' && <ScheduleTab candidate={candidate} />}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} data-testid="toolkit-close">Close</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
